import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { usePathLync } from "@/pathlync/PathLyncAuth";
import { Microscope, LogOut, LayoutDashboard, Loader2 } from "lucide-react";

export const ROLE_LABEL = { lab_user: "Laboratory", pathologist: "Consulting Pathologist" };

export const PathLyncShell = ({ children }) => {
  const { user, signOut, checking } = usePathLync();
  const navigate = useNavigate();
  const location = useLocation();

  if (checking) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50">
        <Loader2 className="animate-spin text-slate-400" size={26} />
      </div>
    );
  }
  if (!user) return <Navigate to="/pathlync" state={{ from: location.pathname }} replace />;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="pathlync-shell">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-5 py-3">
          <Link to="/pathlync/dashboard" className="flex items-center gap-2" data-testid="pathlync-brand">
            <Microscope size={20} className="text-[#1e3a8a]" />
            <span className="font-display text-lg font-bold tracking-tight text-[#1e3a8a]">PathLync</span>
          </Link>
          <span className="hidden text-xs font-medium uppercase tracking-wider text-slate-400 sm:inline">
            Remote consultation
          </span>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-right leading-tight">
              <p className="text-sm font-semibold" data-testid="pathlync-user-name">{user.name}</p>
              <p className="text-xs text-slate-500" data-testid="pathlync-user-role">
                {ROLE_LABEL[user.role]}{user.institution ? ` · ${user.institution}` : ""}
              </p>
            </div>
            <Link to="/pathlync/dashboard" title="Dashboard" data-testid="pathlync-nav-dashboard"
                  className="rounded border border-slate-200 p-2 text-slate-500 hover:border-[#1e3a8a] hover:text-[#1e3a8a]">
              <LayoutDashboard size={16} />
            </Link>
            <button onClick={() => { signOut(); navigate("/pathlync"); }} title="Sign out"
                    data-testid="pathlync-signout-button"
                    className="rounded border border-slate-200 p-2 text-slate-500 hover:border-red-400 hover:text-red-600">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-8">{children}</main>
    </div>
  );
};

export const StatusPill = ({ status }) => {
  const tone = {
    Draft: "bg-slate-200 text-slate-700",
    Submitted: "bg-amber-100 text-amber-900",
    "In Review": "bg-sky-100 text-sky-900",
    Reported: "bg-emerald-100 text-emerald-900",
  }[status] || "bg-slate-200 text-slate-700";
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${tone}`}
          data-testid="consult-status-pill">
      {status}
    </span>
  );
};

export const StatCard = ({ label, value, tone, testid, loading }) => (
  <div className="border border-slate-200 bg-white p-4" data-testid={testid}>
    <p className={`font-display text-3xl font-bold ${tone || "text-[#1e3a8a]"}`}>
      {loading ? <span className="text-slate-300">—</span> : value}
    </p>
    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
  </div>
);
